/* UiPagination — dynamic renderer. Markup comes from #UiPaginationTemplates
   (rendered by snippets/ui-pagination-templates.liquid), the page window is
   the same one snippets/ui-pagination.liquid computes. The caller owns the
   URLs: pass href(page) and, optionally, attrs(page) for data attributes. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.UiPagination = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const SLOTS = 7;

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  // Pages shown at ≤640px: three numbers around the current page.
  function compactRange(current, total) {
    let start = Math.max(1, current - 1);
    if (start + 2 > total) start = Math.max(1, total - 2);
    return [start, Math.min(total, start + 2)];
  }

  // [{ type: 'page', page, wide }, { type: 'gap' }, ...] — numbers and gaps only.
  function items(current, total) {
    total = Math.max(0, Math.floor(total) || 0);
    if (total < 2) return [];
    current = clamp(Math.floor(current) || 1, 1, total);
    const range = compactRange(current, total);
    let pages;
    if (total <= SLOTS) pages = Array.from({ length: total }, (_, i) => i + 1);
    else if (current <= 4) pages = [1, 2, 3, 4, 5, 'gap', total];
    else if (current >= total - 3) pages = [1, 'gap', total - 4, total - 3, total - 2, total - 1, total];
    else pages = [1, 'gap', current - 1, current, current + 1, 'gap', total];
    return pages.map(page => page === 'gap'
      ? { type: 'gap' }
      : { type: 'page', page, wide: page < range[0] || page > range[1] });
  }

  // Single pass, so a value that happens to contain "__page__" is never substituted again.
  function substitute(text, values) {
    return text.replace(/__(\w+?)__/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  // Clones the template element and writes values into attributes and text nodes
  // through the DOM, so a URL with quotes, "<" or "&" cannot change the markup.
  function fill(template, kind, values, wide) {
    const source = template.content.querySelector('[data-pagination-item="' + kind + '"]');
    if (!source) throw new Error('UiPagination template is missing "' + kind + '"');
    const item = document.importNode(source, true);
    [item].concat(Array.from(item.querySelectorAll('*'))).forEach(element => {
      Array.from(element.attributes).forEach(attribute => {
        const value = substitute(attribute.value, values);
        if (value !== attribute.value) element.setAttribute(attribute.name, value);
      });
    });
    const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      node.nodeValue = substitute(node.nodeValue, values);
    }
    if (wide) item.classList.add('Pagination__Item--wide');
    return item;
  }

  function render(options) {
    const template = options.template || document.getElementById('UiPaginationTemplates');
    const total = Math.floor(options.total) || 0;
    if (!template || total < 2) return null;
    const current = clamp(Math.floor(options.current) || 1, 1, total);
    const range = compactRange(current, total);
    const href = options.href;
    const attrs = options.attrs || (() => ({}));

    const link = (kind, page, wide) => {
      const item = fill(template, kind, { page, url: href(page) }, wide);
      const anchor = item.querySelector('a');
      const extra = attrs(page);
      Object.keys(extra).forEach(name => anchor.setAttribute(name, extra[name]));
      return item;
    };

    const nav = document.createElement('nav');
    nav.className = 'Pagination UiPagination' + (options.tight ? ' Pagination--tight' : '');
    nav.setAttribute('aria-label', template.dataset.label || '');
    nav.setAttribute('data-pagination', '');
    nav.setAttribute('data-pagination-current', current);
    nav.setAttribute('data-pagination-total', total);
    const list = document.createElement('ul');
    list.className = 'Pagination__Nav';

    list.append(current > 1 ? link('prev', current - 1) : fill(template, 'prev-off', {}));
    items(current, total).forEach(slot => {
      if (slot.type === 'gap') list.append(fill(template, 'gap', {}));
      else if (slot.page === current) list.append(fill(template, 'current', { page: slot.page }, slot.wide));
      else list.append(link('link', slot.page, slot.wide));
    });
    list.append(current < total ? link('next', current + 1) : fill(template, 'next-off', {}));

    const summary = document.createElement('p');
    summary.className = 'Pagination__Summary';
    summary.textContent = substitute(template.dataset.summary || '', { current, total });
    nav.append(list, summary);
    return nav;
  }

  return { items, render, compactRange };
}));
